<script>
// Fork (US-WA-01) — WhatsApp templates management, styled after AccountHealth.
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CreateTemplate from './templates/CreateTemplate.vue';

const STATUS_COLORS = {
  APPROVED: 'text-n-teal-11',
  PENDING: 'text-n-amber-11',
  REJECTED: 'text-n-ruby-9',
  FLAGGED: 'text-n-amber-11',
  PAUSED: 'text-n-slate-11',
  DISABLED: 'text-n-slate-11',
  PENDING_DELETION: 'text-n-slate-11',
};

export default {
  components: {
    NextButton,
    Icon,
    CreateTemplate,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      templates: [],
      uiFlags: { isFetching: false },
      showCreateModal: false,
    };
  },
  computed: {
    sortedTemplates() {
      return [...this.templates].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    },
  },
  mounted() {
    this.fetchTemplates();
    emitter.on(
      BUS_EVENTS.WHATSAPP_TEMPLATE_STATUS_UPDATED,
      this.onTemplateStatusUpdated
    );
  },
  beforeUnmount() {
    emitter.off(
      BUS_EVENTS.WHATSAPP_TEMPLATE_STATUS_UPDATED,
      this.onTemplateStatusUpdated
    );
  },
  methods: {
    statusColor(status) {
      return STATUS_COLORS[status] || 'text-n-slate-11';
    },
    async fetchTemplates() {
      this.uiFlags.isFetching = true;
      try {
        const { data } = await WhatsappTemplatesAPI.get(this.inbox.id);
        this.templates = data.payload || [];
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.FETCH_ERROR'));
      } finally {
        this.uiFlags.isFetching = false;
      }
    },
    onTemplateStatusUpdated(data) {
      if (Number(data.inbox_id) !== Number(this.inbox.id)) return;

      const index = this.templates.findIndex(
        t =>
          (data.meta_template_id &&
            String(t.id) === String(data.meta_template_id)) ||
          (t.name === data.name && t.language === data.language)
      );

      if (index === -1) {
        // Template not in the current list yet, refetch to stay consistent.
        this.fetchTemplates();
        return;
      }

      this.templates.splice(index, 1, {
        ...this.templates[index],
        status: data.status,
        category: data.category || this.templates[index].category,
        rejected_reason: data.rejected_reason,
        last_status_at: data.last_status_at,
      });
    },
    onTemplateCreated(template) {
      const index = this.templates.findIndex(
        t => String(t.id) === String(template.id)
      );
      if (index === -1) {
        this.templates.push(template);
      } else {
        this.templates.splice(index, 1, template);
      }
      this.showCreateModal = false;
      useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.SUCCESS'));
    },
  },
};
</script>

<template>
  <div class="gap-4 mx-6">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.TITLE') }}
          </span>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.SUBTITLE') }}
          </p>
        </div>
        <div class="flex flex-shrink-0 gap-2 items-center">
          <NextButton
            v-tooltip.top="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.REFRESH')"
            sm
            faded
            slate
            icon="i-lucide-refresh-cw"
            :is-loading="uiFlags.isFetching"
            @click="fetchTemplates"
          />
          <NextButton
            sm
            solid
            blue
            icon="i-lucide-plus"
            :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.CREATE_BUTTON')"
            @click="showCreateModal = true"
          />
        </div>
      </div>

      <woot-loading-state
        v-if="uiFlags.isFetching && !templates.length"
        :message="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.LOADING')"
      />

      <div v-else-if="!templates.length" class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-layout-template" class="mb-2 w-8 h-8" />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.EMPTY') }}
            </p>
          </div>
        </div>
      </div>

      <div v-else class="grid grid-cols-1 gap-4 xs:grid-cols-2">
        <div
          v-for="template in sortedTemplates"
          :key="template.id || template.name"
          class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-3 justify-between items-center">
            <span class="font-medium truncate text-body-main text-n-slate-12">
              {{ template.name }}
            </span>
            <span
              class="inline-flex flex-shrink-0 items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
              :class="statusColor(template.status)"
            >
              {{ template.status }}
            </span>
          </div>
          <div class="flex gap-6 items-center">
            <div class="flex flex-col gap-0.5">
              <span class="text-label-small text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.CATEGORY') }}
              </span>
              <span class="text-label text-n-slate-12">
                {{ template.category }}
              </span>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-label-small text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.LANGUAGE') }}
              </span>
              <span class="text-label text-n-slate-12">
                {{ template.language }}
              </span>
            </div>
          </div>
          <p
            v-if="template.rejected_reason"
            class="flex gap-1.5 items-center text-label-small text-n-ruby-9"
          >
            <Icon
              icon="i-lucide-alert-triangle"
              class="flex-shrink-0 w-3.5 h-3.5"
            />
            {{ template.rejected_reason }}
          </p>
        </div>
      </div>
    </div>

    <woot-modal
      v-model:show="showCreateModal"
      :on-close="() => (showCreateModal = false)"
    >
      <CreateTemplate
        :inbox="inbox"
        :existing-templates="templates"
        @created="onTemplateCreated"
        @close="showCreateModal = false"
      />
    </woot-modal>
  </div>
</template>
